import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user
  const [loading, setLoading] = useState(true);
  const loggedInRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      if (!loggedInRef.current) {
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (username, password, remember = false) => {
    try {
      // Diz ao registo de acessos se o login veio da app instalada (PWA).
      const standalone = window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
      const { data } = await api.post(
        "/auth/login",
        { username, password, remember },
        { headers: { "X-Display-Mode": standalone ? "standalone" : "browser" } },
      );
      // As chaves do cache ("tasks/mine", "scratchpad"…) não levam o id do
      // utilizador: sem limpar, quem entra a seguir no mesmo browser via os
      // dados da conta anterior até expirarem.
      queryClient.clear();
      loggedInRef.current = true;
      setLoading(false);
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: apiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    loggedInRef.current = false;
    queryClient.clear();
    setUser(false);
  };

  const updateUser = (u) => setUser(u);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
